import { useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { KeyRound } from 'lucide-react'
import { changeOwnPassword } from '@/api/auth'
import { useAuth } from '@/hooks/useAuth'
import { errorMessage } from '@/lib/apiError'
import { Button } from './Button'
import { PasswordInput } from './PasswordInput'

/**
 * Forced password change. When HR set a temporary password (users.must_change_password), the
 * API refuses everything else with 403 PASSWORD_CHANGE_REQUIRED, so this replaces the page with
 * the change form until a new password is saved and the server confirms the flag is cleared.
 */
export function ForcePasswordChangeBanner() {
  const { user, logout } = useAuth()
  const qc = useQueryClient()
  const [cur, setCur] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  if (!user?.must_change_password) return null

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setErr(null)
    if (next.length < 8) return setErr('The new password must be at least 8 characters.')
    if (next !== confirm) return setErr('The two new passwords do not match.')
    setBusy(true)
    try {
      await changeOwnPassword(cur, next, confirm)
      // Success only once the server's own record says the flag is cleared.
      await qc.invalidateQueries({ queryKey: ['auth', 'me'] })
      const fresh = qc.getQueryData<{ must_change_password?: boolean } | null>(['auth', 'me'])
      if (fresh?.must_change_password) setErr('The password was not updated. Please try again.')
      else await qc.invalidateQueries()
    } catch (x) {
      setErr(errorMessage(x))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="force-pw-title" data-testid="force-password-change"
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-background p-4">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-card p-6 shadow-lg">
        <div className="flex items-center gap-2">
          <KeyRound className="size-5 text-primary" aria-hidden />
          <h1 id="force-pw-title" className="text-lg font-semibold">Set a new password</h1>
        </div>
        <p className="text-sm text-muted-foreground">Your administrator set a temporary password for you. Choose your own before continuing.</p>
        <label className="block text-sm">Temporary password
          <PasswordInput value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" required className="mt-1" />
        </label>
        <label className="block text-sm">New password
          <PasswordInput value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" required className="mt-1" />
        </label>
        <label className="block text-sm">Confirm new password
          <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required className="mt-1" />
        </label>
        {err && <p role="alert" className="text-sm text-destructive">{err}</p>}
        <div className="flex items-center justify-between gap-2">
          <Button type="button" variant="ghost" onClick={() => void logout()}>Sign out</Button>
          <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save password'}</Button>
        </div>
      </form>
    </div>
  )
}
